import { Spinner } from "@mixitone/components";
import { Club, RecurringSession } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { RoutedLink } from "components/common/Link";
import Form from "./Form";
import { FormController } from "./FormController";
import type { FormFieldConfig } from "./FormFieldsDefinition";
import { ModalFormFieldset } from "./FormRenderers/ModalFormRenderer";
import FormButtons from "./FormButtons";

class RecurringSessionFormController extends FormController<
  RecurringSession,
  { club_id: string },
  { club: Club }
> {
  override async onLoad() {
    this.state.club = await Club.query().find(this.state.attributes.club_id);
  }

  override findModel(id: string) {
    return RecurringSession.query().find(id);
  }
  override async buildRecord(): Promise<RecurringSession> {
    return new RecurringSession({
      weekday: 1,
      courts: 5,
      start_time: "18:00",
      end_time: "20:00",
      club_id: this.state.attributes.club_id,
    });
  }

  override saveRecord(record: RecurringSession): Promise<void> {
    record.club_id = this.state.attributes.club_id;
    return record.save();
  }

  override get canDelete(): boolean {
    return this.state.club?.hasManagePermission();
  }

  override get canSave(): boolean {
    return this.state.club?.hasManagePermission();
  }

  fields() {
    return {
      weekday: {
        name: "weekday",
        type: "select",
        label: "Weekday",
        required: true,
        options: RecurringSession.WEEKDAYS.map((weekday, index) => ({
          value: index + 1,
          label: weekday,
        })),
      },
      start_time: {
        name: "start_time",
        type: "time",
        label: "Start time",
        required: true,
      },
      end_time: {
        name: "end_time",
        type: "time",
        label: "End time",
        required: true,
      },
      courts: {
        name: "courts",
        type: "number",
        label: "Courts",
        required: true,
        min: 1,
      },
      player_fee: {
        name: "player_fee",
        type: "number",
        label: "Player fee",
        min: 0,
        max: 999,
        step: 1,
        prefix: "$",
      },
      description: {
        name: "description",
        type: "text",
        label: "Description",
        inputType: "textarea",
        description: this.state.club?.listed_in_directory
          ? "This description will be shown in the public directory for this session."
          : undefined,
      },
      listed_in_directory: {
        name: "listed_in_directory",
        type: "checkbox",
        label: "List in public directory",
        description: this.state.club?.listed_in_directory ? undefined : (
          <p className="text-sm text-gray-600">
            The club must be listed in the{" "}
            <RoutedLink
              kind="link"
              to="/settings/clubs/:clubId/public-directory"
              params={{ clubId: this.state.club?.id ?? "" }}
              className="text-blue-600 hover:underline"
            >
              public directory
            </RoutedLink>
            .
          </p>
        ),
        disabled: !this.state.club?.listed_in_directory,
      },
    } as const satisfies Record<string, FormFieldConfig>;
  }
}

const RecurringSessionForm: React.FC = () => {
  const controller = RecurringSessionFormController.use();
  const { loading } = controller.state;
  const { canSave } = controller;
  const fields = Object.values(controller.fields());

  if (loading) return <Spinner size={64} />;

  return (
    <Form name="event">
      <ModalFormFieldset fields={fields} />
      <FormButtons showDelete={true} disabled={!canSave} />
    </Form>
  );
};

export default RecurringSessionFormController.scope(ApplicationView(RecurringSessionForm));
